<script lang="ts" generics="Item">
  import {
    type ComboboxOptionProps,
    createCombobox,
    melt,
  } from "@melt-ui/svelte";
  import { Check, ChevronDown } from "lucide-svelte";
  import { fly } from "svelte/transition";

  interface Props {
    /** Label at the top of the input. */
    title: string;
    /** Placeholder text before an item is selected. */
    placeholder: string;
    /** List of generic items. */
    items: Item[];
    /** How each item displays as a string in the dropdown. */
    getItemText: (item: Item) => string;
    /** Bindable current selected value of the input. */
    value: Item | undefined;
    /** Limits the combobox to width of the input. By default, it stretches to fit contents. */
    sameWidth?: boolean;
    /** Disable the input. */
    disabled?: boolean;
  }

  let {
    title,
    placeholder,
    items,
    getItemText,
    value = $bindable(),
    sameWidth = false,
    disabled = false,
  }: Props = $props();

  // Width of the input element, used to set the min-width of the dropdown when
  // `sameWidth` is not used on createCombobox().
  let inputWidth = $state<number>();

  const toOption = (item: Item): ComboboxOptionProps<Item> => ({
    value: item,
    label: getItemText(item),
  });

  const {
    elements: { menu, input, option, label },
    states: { open, inputValue, touchedInput, selected },
    helpers: { isSelected },
  } = createCombobox<Item>({
    forceVisible: true,
    positioning: {
      placement: "bottom-start", // Open below, flush to the left edge of input.
      sameWidth,
      gutter: 10,
    },
  });

  // When the selected item changes, update the value which the parent component binds to
  $effect(() => {
    value = $selected?.value;
  });

  // When the parent component changes the value prop, update the selected value
  $effect(() => {
    $selected = value ? toOption(value) : undefined;
  });

  $effect(() => {
    if (!$open) {
      $inputValue = $selected?.label ?? "";
    }
  });

  const filteredItems = $derived.by(() => {
    if (!$touchedInput) return items;
    const normalizedInput = $inputValue.toLowerCase();
    return items.filter((item) => {
      const normalizedLabel = getItemText(item).toLowerCase();
      return normalizedLabel.includes(normalizedInput);
    });
  });
</script>

<div class="flex flex-col gap-1 group">
  <label use:melt={$label}>
    <span class="text-sm text-white/60">{title}</span>
  </label>

  <div class="relative">
    <input
      use:melt={$input}
      class={{
        "flex h-10 w-full items-center justify-between rounded-lg bg-zinc-800 px-3 pr-10 text-sm text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors": true,
        "group-hover:bg-white/10": !disabled,
      }}
      {placeholder}
      {disabled}
      bind:clientWidth={inputWidth}
    />
    <div
      class="pointer-events-none absolute right-3 top-1/2 z-10 -translate-y-1/2 text-white/60 transition-transform"
      class:rotate-180={$open}
    >
      <ChevronDown size={16} />
    </div>
  </div>
</div>
{#if $open}
  <ul
    class="z-10 flex flex-col overflow-hidden max-h-[300px] rounded-lg border border-white/10 bg-ground/50 backdrop-blur-md"
    style:min-width={inputWidth ? `${inputWidth}px` : undefined}
    use:melt={$menu}
    transition:fly={{ duration: 150, y: -5 }}
  >
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div
      class="flex flex-col max-h-full overflow-y-auto p-2 gap-y-1"
      tabindex="0"
    >
      {#each filteredItems as item, index (index)}
        {@const active = $isSelected(item)}
        <li
          use:melt={$option(toOption(item))}
          class="relative cursor-pointer scroll-my-1 rounded-md py-1.5 px-2 text-sm text-white outline-none
          hover:bg-white/10
          data-[highlighted]:bg-white/10
          data-[selected]:bg-primary/10
          data-[disabled]:opacity-50"
        >
          <span class="block truncate {active ? 'pr-6' : ''}"
            >{getItemText(item)}</span
          >
          {#if active}
            <div class="absolute right-2 top-1/2 z-10 -translate-y-1/2">
              <Check color="#7FEE64" size={16} />
            </div>
          {/if}
        </li>
      {:else}
        <li class="relative px-2 py-1.5 text-sm text-white/60">
          No results found
        </li>
      {/each}
    </div>
  </ul>
{/if}
